import { startCase } from 'lodash';
import styled from 'styled-components';

import { colors } from 'styles/theme/colors';
import { BodySmall, BodySmallBold } from 'styles/typography';

import { SectionCard, SectionCardHeader, SectionCardHint, SectionCardTitle } from './BenefitsTable';
import { SkilledNursingSummary, snfBucketReported, snfBucketRows } from './eligibilityData';

// The skilled-nursing section — the benefit that matters most for a SNF referral. Rendered from
// the backend-decoded summary.skilledNursing (STC AG/AH), per-network, with an honest
// "not reported" state when the payer volunteered nothing.

interface Props {
  snf?: SkilledNursingSummary;
}

export default function SkilledNursingSection({ snf }: Props) {
  const codes = snf?.serviceTypeCodesSeen ?? [];
  const codeLabel = codes.length ? `STC ${codes.join(', ')}` : 'STC AG / AH';

  const networks = [
    { label: 'In-network', bucket: snf?.inNetwork },
    { label: 'Out-of-network', bucket: snf?.outNetwork },
  ].filter((n) => snfBucketReported(n.bucket));

  const reported = !!snf?.skilledNursingReported && networks.length > 0;

  return (
    <SectionCard data-testid='eligibility-snf'>
      <SectionCardHeader>
        <SectionCardTitle>Skilled Nursing</SectionCardTitle>
        <SectionCardHint>{codeLabel}</SectionCardHint>
      </SectionCardHeader>

      {reported ? (
        <Networks>
          {networks.map((n) => (
            <NetworkBlock key={n.label}>
              <NetworkTitle>{n.label}</NetworkTitle>
              <Rows>
                {snfBucketRows(n.bucket).map((row) => (
                  <Row key={row.label}>
                    <RowLabel>{row.label}</RowLabel>
                    <RowValue>{row.value}</RowValue>
                  </Row>
                ))}
              </Rows>
              {(n.bucket?.notes ?? []).length > 0 && (
                <Notes>
                  {n.bucket!.notes!.map((note, i) => (
                    <li key={i}>{note}</li>
                  ))}
                </Notes>
              )}
            </NetworkBlock>
          ))}
        </Networks>
      ) : (
        <NotReported>
          Skilled nursing benefits were not reported by this payer
          {snf?.reason ? ` (${startCase(snf.reason)})` : ''}.
        </NotReported>
      )}
    </SectionCard>
  );
}

const Networks = styled.div`
  display: flex;
  flex-wrap: wrap;
`;

const NetworkBlock = styled.div`
  flex: 1 1 260px;
  padding: 20px 24px;

  & + & {
    border-left: 1px solid ${colors.black10};
  }

  @media (max-width: 640px) {
    & + & {
      border-left: none;
      border-top: 1px solid ${colors.black10};
    }
  }
`;

const NetworkTitle = styled(BodySmallBold)`
  display: block;
  margin-bottom: 12px;
  color: ${colors.black100};
`;

const Rows = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: 120px 1fr;
  gap: 12px;
`;

const RowLabel = styled(BodySmall)`
  color: ${colors.black50};
`;

const RowValue = styled(BodySmall)`
  color: ${colors.black100};
`;

const Notes = styled.ul`
  margin: 12px 0 0;
  padding-left: 18px;
  color: ${colors.black75};
  font-size: 13px;
`;

const NotReported = styled(BodySmall)`
  display: block;
  padding: 20px 24px;
  color: ${colors.black50};
`;
